import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  ShieldCheck, ShieldAlert, ShieldX, RefreshCw, Loader2, 
  Truck, Users, AlertTriangle, CheckCircle, XCircle, Building,
  Phone, MapPin, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const FMCSAVerificationCard = ({ dotNumber, isOwnProfile = false, userId = null }) => {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [verifyDot, setVerifyDot] = useState(dotNumber || '');
  const [showVerifyForm, setShowVerifyForm] = useState(false);

  useEffect(() => {
    if (dotNumber || userId) {
      loadVerification();
    }
  }, [dotNumber, userId]);

  const loadVerification = async () => {
    setLoading(true);
    try {
      const endpoint = isOwnProfile 
        ? '/fmcsa/my-verification'
        : `/fmcsa/verification/${userId}`;
      const response = await api.get(endpoint);
      setVerification(response.data);
    } catch (error) {
      // No verification found is expected for new users
      if (error.response?.status !== 404) {
        console.error('Failed to load FMCSA verification:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verifyDot) {
      toast.error('Please enter a DOT number');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/fmcsa/verify', { dot_number: verifyDot });
      setVerification(response.data);
      setShowVerifyForm(false);
      
      if (response.data.verification_status === 'verified') {
        toast.success('Carrier verified successfully!');
      } else if (response.data.verification_status === 'out_of_service') {
        toast.warning('Carrier is out of service');
      } else {
        toast.error('Carrier verification failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/fmcsa/refresh');
      await loadVerification();
      toast.success('Verification refreshed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-medium">FMCSA Verified</span>
          </div>
        );
      case 'out_of_service':
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
            <ShieldX className="w-4 h-4" />
            <span className="font-medium">Out of Service</span>
          </div>
        );
      case 'not_found':
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            <ShieldAlert className="w-4 h-4" />
            <span className="font-medium">Not Found</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
            <ShieldAlert className="w-4 h-4" />
            <span className="font-medium">Pending Verification</span>
          </div>
        );
    }
  };

  const getSafetyRatingBadge = (rating) => {
    if (!rating) return null;
    const colors = {
      'Satisfactory': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Conditional': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Unsatisfactory': 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${colors[rating] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
        {rating}
      </span>
    );
  };

  if (loading && !verification) {
    return (
      <Card className="bg-[#112240] border-[#3B82F6]/20">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#60A5FA]" />
        </CardContent>
      </Card>
    );
  }

  // Show verify form if no verification exists and it's own profile
  if (!verification && isOwnProfile) {
    return (
      <Card className="bg-[#112240] border-[#3B82F6]/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#60A5FA]" />
            FMCSA Verification
          </CardTitle>
          <CardDescription>
            Verify your carrier authority with FMCSA to build trust
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showVerifyForm ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>DOT Number</Label>
                <Input
                  placeholder="Enter your DOT number"
                  value={verifyDot}
                  onChange={(e) => setVerifyDot(e.target.value)}
                  className="bg-[#0A1628] border-[#3B82F6]/20"
                  data-testid="fmcsa-dot-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleVerify} 
                  disabled={loading}
                  className="bg-[#4ADE80] hover:bg-[#22C55E] text-black"
                  data-testid="fmcsa-verify-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                  Verify
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowVerifyForm(false)}
                  className="border-[#3B82F6]/30"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button 
              onClick={() => setShowVerifyForm(true)}
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              data-testid="fmcsa-start-verify-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Verify Your DOT Number
            </Button>
          )}
          <p className="text-xs text-[#64748B]">
            We use the official FMCSA database to verify your carrier authority, safety rating, and insurance status.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show verification not available for other profiles
  if (!verification && !isOwnProfile) {
    return (
      <Card className="bg-[#112240] border-[#3B82F6]/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#94A3B8]" />
            FMCSA Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[#94A3B8]">No FMCSA verification on file</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#112240] border-[#3B82F6]/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#60A5FA]" />
            FMCSA Verification
          </CardTitle>
          {isOwnProfile && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-[#60A5FA] hover:text-[#93C5FD]"
              data-testid="fmcsa-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3 mt-2">
          {getStatusBadge(verification.verification_status)}
          {verification.safety_rating && getSafetyRatingBadge(verification.safety_rating)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Company Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
              <Building className="w-4 h-4" />
              Legal Name
            </div>
            <p className="text-white font-medium">{verification.legal_name || '-'}</p>
            {verification.dba_name && (
              <p className="text-[#94A3B8] text-sm">DBA: {verification.dba_name}</p>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 text-[#64748B] text-sm mb-1">
              <FileText className="w-4 h-4" />
              Authority Numbers
            </div>
            <p className="text-white">DOT: {verification.dot_number}</p>
            {verification.mc_number && (
              <p className="text-[#94A3B8] text-sm">MC: {verification.mc_number}</p>
            )}
          </div>
        </div>

        {/* Status Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
            <div className="flex items-center gap-2 mb-1">
              {verification.allow_to_operate ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400" />
              )}
              <span className="text-xs text-[#64748B]">Authority</span>
            </div>
            <p className={`text-sm font-medium ${verification.allow_to_operate ? 'text-green-400' : 'text-red-400'}`}>
              {verification.allow_to_operate ? 'Active' : 'Inactive'}
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
            <div className="flex items-center gap-2 mb-1">
              {verification.bipd_insurance_on_file === 'Y' ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
              )}
              <span className="text-xs text-[#64748B]">BIPD Insurance</span>
            </div>
            <p className={`text-sm font-medium ${verification.bipd_insurance_on_file === 'Y' ? 'text-green-400' : 'text-yellow-400'}`}>
              {verification.bipd_insurance_on_file === 'Y' ? 'On File' : 'Not on File'}
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
            <div className="flex items-center gap-2 mb-1">
              {verification.cargo_insurance_on_file === 'Y' ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
              )}
              <span className="text-xs text-[#64748B]">Cargo Insurance</span>
            </div>
            <p className={`text-sm font-medium ${verification.cargo_insurance_on_file === 'Y' ? 'text-green-400' : 'text-yellow-400'}`}>
              {verification.cargo_insurance_on_file === 'Y' ? 'On File' : 'Not on File'}
            </p>
          </div>
          
          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
            <div className="flex items-center gap-2 mb-1">
              {!verification.out_of_service ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400" />
              )}
              <span className="text-xs text-[#64748B]">OOS Status</span>
            </div>
            <p className={`text-sm font-medium ${!verification.out_of_service ? 'text-green-400' : 'text-red-400'}`}>
              {!verification.out_of_service ? 'Clear' : 'Out of Service'}
            </p>
          </div>
        </div>

        {/* Fleet Info */}
        {(verification.total_power_units || verification.total_drivers) && (
          <div className="flex gap-6 p-3 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
            {verification.total_power_units && (
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#60A5FA]" />
                <div>
                  <p className="text-lg font-bold text-white">{verification.total_power_units}</p>
                  <p className="text-xs text-[#64748B]">Power Units</p>
                </div>
              </div>
            )}
            {verification.total_drivers && (
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#60A5FA]" />
                <div>
                  <p className="text-lg font-bold text-white">{verification.total_drivers}</p>
                  <p className="text-xs text-[#64748B]">Drivers</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Crash Data */}
        {verification.crash_total !== null && verification.crash_total !== undefined && (
          <div className="p-3 rounded-lg bg-[#0A1628] border border-[#3B82F6]/10">
            <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              Crash History (24 months)
            </h4>
            <div className="flex gap-4 text-sm">
              <span className="text-[#94A3B8]">Total: <span className="text-white font-medium">{verification.crash_total}</span></span>
              {verification.fatal_crash !== null && (
                <span className="text-[#94A3B8]">Fatal: <span className="text-red-400 font-medium">{verification.fatal_crash}</span></span>
              )}
              {verification.injury_crash !== null && (
                <span className="text-[#94A3B8]">Injury: <span className="text-yellow-400 font-medium">{verification.injury_crash}</span></span>
              )}
            </div>
          </div>
        )}

        {/* Location */}
        {(verification.physical_city || verification.physical_state) && (
          <div className="flex items-center gap-2 text-[#94A3B8]">
            <MapPin className="w-4 h-4" />
            <span>{[verification.physical_city, verification.physical_state].filter(Boolean).join(', ')}</span>
          </div>
        )}

        {/* Last Verified */}
        <p className="text-xs text-[#64748B]">
          Last verified: {new Date(verification.last_verified_at).toLocaleDateString()}
        </p>
      </CardContent>
    </Card>
  );
};

export default FMCSAVerificationCard;
